"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "@/i18n/client";
import { SearchPanel } from "./components/SearchPanel";
import styles from "./SearchModal.module.css";
import { useSearchModal } from "./SearchProvider";

const CLOSE_ANIMATION_MS = 220;

export const SearchModal = () => {
	const t = useTranslations();
	const { isOpen, close } = useSearchModal();
	const [mounted, setMounted] = useState(false);

	// Lazy-mount SearchPanel only while the modal is open (or animating closed),
	// so its react-query hooks (useSearchResults, usePopularProducts, etc.) don't
	// run on every page load when the modal is dormant.
	useEffect(() => {
		if (isOpen) {
			setMounted(true);
			return;
		}
		const timer = setTimeout(() => setMounted(false), CLOSE_ANIMATION_MS);
		return () => clearTimeout(timer);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") close();
		};
		document.addEventListener("keydown", handler);
		document.body.classList.add("scroll-locked");
		return () => {
			document.removeEventListener("keydown", handler);
			document.body.classList.remove("scroll-locked");
		};
	}, [isOpen, close]);

	return (
		<>
			<div aria-hidden="true" data-open={isOpen} onClick={close} className={styles.backdrop} />
			<div
				role="dialog"
				aria-modal="true"
				aria-label={t("Search")}
				data-open={isOpen}
				className={styles.panel}
			>
				{mounted && <SearchPanel open={isOpen} onClose={close} />}
			</div>
		</>
	);
};
